/**
 * AdminOffersPage
 * -----------------------
 * This page allows admins to manage store offers/promotions.
 * Features:
 *  - Create new offers with message, active status, and optional date range
 *  - Enable/disable existing offers
 *  - Delete offers
 *  - Fetches and stores data in the Supabase "offers" table
 * 
 * NOTE: Only styling applied is preserved—no design changes were made.
 */

import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

type Offer = {
  id: number;
  message: string;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  created_at: string;
  updated_at: string;
};

const AdminOffersPage: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [message, setMessage] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  const fetchOffers = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from("offers")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to fetch offers:", error.message);
    } else {
      setOffers((data as Offer[]) || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const addOffer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    const { error } = await supabase.from("offers").insert({
      message: message.trim(),
      is_active: isActive,
      starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
    });

    if (error) {
      console.error("Failed to add offer:", error.message);
      return;
    }

    setMessage("");
    setIsActive(true);
    setStartsAt("");
    setEndsAt("");

    fetchOffers();
  };

  const toggleActive = async (offer: Offer) => {
    const { error } = await supabase
      .from("offers")
      .update({ is_active: !offer.is_active })
      .eq("id", offer.id);

    if (error) {
      console.error("Failed to update offer:", error.message);
      return;
    }

    fetchOffers();
  };

  const deleteOffer = async (id: number) => {
    const { error } = await supabase.from("offers").delete().eq("id", id);

    if (error) {
      console.error("Failed to delete offer:", error.message);
      return;
    }

    setOffers((prev) => prev.filter((o) => o.id !== id));
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-[#5c2f2f] mb-6">Offers</h1>

      {/* Create Offer Form */}
      <form
        onSubmit={addOffer}
        className="border border-[rgba(92,47,47,0.2)] rounded-xl p-4 bg-white/80 backdrop-blur-sm mb-8 space-y-4"
      >
        <div>
          <label className="block text-[#5c2f2f] font-semibold mb-1">
            Offer message
          </label>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full rounded-lg border border-[#e6cccc] p-2 outline-none focus:ring-2 focus:ring-[#5c2f2f]/30"
            placeholder='e.g., "Free shipping over $50"'
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <input
              id="isActive"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="isActive" className="text-[#5c2f2f]">
              Active
            </label>
          </div>

          <div>
            <label className="block text-[#5c2f2f] font-semibold mb-1">
              Starts at (optional)
            </label>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full rounded-lg border border-[#e6cccc] p-2"
            />
          </div>

          <div>
            <label className="block text-[#5c2f2f] font-semibold mb-1">
              Ends at (optional)
            </label>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="w-full rounded-lg border border-[#e6cccc] p-2"
            />
          </div>
        </div>

        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-[#5c2f2f] text-white hover:bg-[#4e2828] transition"
        >
          Add offer
        </button>
      </form>

      {/* Offer List */}
      {loading ? (
        <p className="text-[#7a4343]">Loading...</p>
      ) : offers.length === 0 ? (
        <p className="text-[#7a4343]">No offers yet.</p>
      ) : (
        <div className="space-y-3">
          {offers.map((o) => (
            <div
              key={o.id}
              className="flex items-center justify-between border border-[rgba(92,47,47,0.2)] rounded-lg p-3 bg-white/80"
            >
              <div className="min-w-0">
                <p className="text-[#5c2f2f] font-medium truncate">
                  {o.message}
                </p>
                <p className="text-xs text-[#7a4343]">
                  {o.is_active ? "Active" : "Inactive"}
                  {o.starts_at &&
                    ` • Starts: ${new Date(o.starts_at).toLocaleString()}`}
                  {o.ends_at &&
                    ` • Ends: ${new Date(o.ends_at).toLocaleString()}`}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleActive(o)}
                  className="px-3 py-1 rounded-md border border-[#e6cccc] text-[#5c2f2f] hover:bg-[#fff0f0]"
                >
                  {o.is_active ? "Disable" : "Enable"}
                </button>

                <button
                  onClick={() => deleteOffer(o.id)}
                  className="px-3 py-1 rounded-md border border-[#e6cccc] text-[#7a4343] hover:bg-[#fff0f0]"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOffersPage;
